// Shared status badge and accent config — single source of truth for
// TriageCard, SurgeryPanel, and any future review panel components.

export type StatusKey =
  | 'verified'
  | 'partial'
  | 'unverified'
  | 'unsupported'
  | 'contradicted'
  | 'error'

export const statusBadgeConfig: Record<string, { label: string; className: string }> = {
  verified:     { label: 'Verified',           className: 'bg-[var(--status-verified-bg)] text-[var(--status-verified-text)]' },
  partial:      { label: 'Partially verified', className: 'bg-[var(--status-partial-bg)] text-[var(--status-partial-text)]' },
  unverified:   { label: 'Unverified',         className: 'bg-[var(--status-partial-bg)] text-[var(--status-partial-text)]' },
  unsupported:  { label: 'Unsupported',        className: 'bg-amber-100 text-amber-800' },
  contradicted: { label: 'Contradicted',       className: 'bg-[var(--status-error-bg)] text-[var(--status-error-text)]' },
  error:        { label: 'Error',              className: 'bg-[var(--status-error-bg)] text-[var(--status-error-text)]' },
}

export const accentBorderColor: Record<string, string> = {
  verified:     'var(--status-verified-border)',
  partial:      'var(--status-partial-border)',
  unverified:   'var(--status-partial-border)',
  unsupported:  '#d97706',
  contradicted: 'var(--status-error-border)',
  error:        'var(--status-error-border)',
}

export const supportLevelBadge: Record<string, { label: string; className: string; borderClass: string }> = {
  supports:           { label: 'Supports',           className: 'bg-emerald-100 text-emerald-700', borderClass: 'border-l-emerald-400' },
  partially_supports: { label: 'Partially Supports', className: 'bg-amber-100 text-amber-700',    borderClass: 'border-l-amber-400' },
  contradicts:        { label: 'Contradicts',        className: 'bg-red-100 text-red-700',         borderClass: 'border-l-red-400' },
}

// ── Types for source-removal badge recalculation ────────────────────────────

export type VerificationStatus = 'verified' | 'partial' | 'unverified' | 'contradicted' | 'unsupported'

export interface ClaimSourceEntry {
  source_doc: string
  support_level: 'supports' | 'partially_supports' | 'contradicts' | 'not_relevant'
  reasoning?: string
}

/**
 * Mirrors backend claim-status semantics (agentic_flow.verify_and_map +
 * the synthetic "unsupported" card in main.py). Pure — no side effects.
 *
 * `not_relevant` sources don't address the claim; a claim whose only
 * remaining active sources are `not_relevant` stays `unsupported`, matching
 * the backend rule "all sources returned not_relevant → unsupported."
 */
export function recalculateStatus(activeSources: ClaimSourceEntry[]): VerificationStatus {
  if (activeSources.length === 0) return 'unverified'
  if (activeSources.some(s => s.support_level === 'contradicts')) return 'contradicted'
  if (activeSources.every(s => s.support_level === 'not_relevant')) return 'unsupported'
  const addressing = activeSources.filter(s => s.support_level !== 'not_relevant')
  if (addressing.every(s => s.support_level === 'supports')) return 'verified'
  return 'partial'
}

/**
 * Generate a client-side analysis summary when sources are removed/restored.
 * V1 heuristic — avoids an API round-trip.
 */
export function generateAnalysisUpdate(
  activeSources: ClaimSourceEntry[],
  hiddenSources: ClaimSourceEntry[],
  newStatus: VerificationStatus,
  getLabel: (sourceDoc: string) => string,
): string {
  if (hiddenSources.length === 0) return '' // caller should use originalAnalysis

  const supportCount = activeSources.filter(s => s.support_level === 'supports').length
  const partialCount = activeSources.filter(s => s.support_level === 'partially_supports').length
  const contradictCount = activeSources.filter(s => s.support_level === 'contradicts').length
  const hiddenLabels = hiddenSources.map(s => getLabel(s.source_doc)).join(', ')

  let text = ''
  if (newStatus === 'verified') {
    text = `${supportCount} remaining source${supportCount !== 1 ? 's' : ''} fully support${supportCount === 1 ? 's' : ''} this claim.`
  } else if (newStatus === 'contradicted') {
    text = `${contradictCount} source${contradictCount !== 1 ? 's' : ''} still contradict${contradictCount === 1 ? 's' : ''} this claim.`
    if (supportCount > 0) text += ` ${supportCount} source${supportCount !== 1 ? 's' : ''} support it.`
  } else if (newStatus === 'partial') {
    text = `Evidence is mixed: ${supportCount} supporting, ${partialCount} partially supporting.`
  } else if (newStatus === 'unsupported') {
    const notRelevantCount = activeSources.length
    text = `${notRelevantCount} remaining source${notRelevantCount !== 1 ? 's' : ''} did not address this claim directly.`
  } else {
    text = 'No active sources remain for this claim.'
  }

  text += ` Excluded by reviewer: ${hiddenLabels}.`
  return text
}
